import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useClientStore } from '@/client/stores/client';
import { useDataScopeStore } from '@/client/stores/data-scope';

/**
 * Download every row matching the current filters as CSV (`?format=csv` on the
 * list endpoint). Uses fetch directly because `request()` parses JSON; paging
 * params are dropped so the export is the whole filtered set, not one page.
 */
export function ExportCsvButton({
  path,
  params,
  filename,
}: {
  path: string;
  params?: object;
  filename: string;
}) {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams({ format: 'csv' });
      Object.entries(params ?? {}).forEach(([k, v]) => {
        if (k === 'limit' || k === 'offset' || v === undefined || v === null || v === '') return;
        qs.set(k, String(v));
      });
      const res = await fetch(`${path}?${qs}`, {
        credentials: 'include',
        cache: 'no-store',
        headers: {
          ...((useClientStore.getState().defaultHeaders ?? {}) as Record<string, string>),
          'x-admin-data-scope': useDataScopeStore.getState().scope,
        },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status}).`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      const rows = res.headers.get('x-export-rows');
      if (res.headers.get('x-export-truncated') === '1') {
        toast.warning(`Exported the first ${rows} rows. Narrow the filters to get the rest.`, {
          richColors: true,
        });
      } else if (rows) {
        toast.success(`Exported ${Number(rows).toLocaleString()} rows`, { richColors: true });
      }
    } catch (e: any) {
      toast.error(e?.message || 'Export failed', { richColors: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button type="button" variant="outline" size="sm" disabled={busy} onClick={download}>
      {busy ? (
        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
      ) : (
        <Download className="mr-1.5 h-4 w-4" />
      )}
      Export CSV
    </Button>
  );
}
